import React, { useState } from "react";
import { FaEdit } from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import {
  formatDate,
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import { handicapFormErrors } from "../../utility/handicapFieldValidation";
import {
  SCORING_HANDICAP,
  formatTournamentHandicap,
  handicapFieldsFromRow,
  handicapValuesForSave,
} from "../../utility/tournamentScoringType";
import { ScoringTypeToggle } from "../CreateTournamentForm/HandicapFields";
import { requestTournamentIndexNow } from "../../utility/requestIndexNow";

const emptyToNullInt = (value) => (value === "" ? null : parseInt(value, 10));

const fieldsFromTournament = (tournament) => ({
  name: tournament.name || "",
  startDate: tournament.startDate || "",
  ...handicapFieldsFromRow(tournament),
  payoutRatioNumerator: tournament.payoutRatioNumerator ?? "",
  payoutRatioDenominator: tournament.payoutRatioDenominator ?? "",
});

const inputClass =
  "w-full p-3 border border-brand-charcoal/20 rounded-lg focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent";
const smallInputClass =
  "w-full p-2 text-sm border border-brand-charcoal/20 rounded-lg focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent";

const DetailRow = ({ label, value }) => (
  <div>
    <div className="text-xs font-medium text-brand-muted mb-1">{label}</div>
    <div className="text-sm text-brand-charcoal">{value}</div>
  </div>
);

const TournamentSettingsForm = ({ tournament, onSaved, framed = true }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [fields, setFields] = useState(() => fieldsFromTournament(tournament));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const setField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleStartEdit = () => {
    setFields(fieldsFromTournament(tournament));
    setErrorMessage("");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(fieldsFromTournament(tournament));
    setErrorMessage("");
    setIsEditing(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const scoringErrors = handicapFormErrors(fields);
    const firstError = Object.values(scoringErrors).find(Boolean);
    if (firstError) {
      setErrorMessage(firstError);
      return;
    }

    setSaving(true);
    setErrorMessage("");

    const nextValues = {
      name: fields.name.trim(),
      startDate: fields.startDate || null,
      ...handicapValuesForSave(fields),
      payoutRatioNumerator: emptyToNullInt(fields.payoutRatioNumerator),
      payoutRatioDenominator: emptyToNullInt(fields.payoutRatioDenominator),
    };

    try {
      const { error } = await supabase
        .from("tournaments")
        .update(nextValues)
        .eq("id", tournament.id);

      if (error) throw error;
      void requestTournamentIndexNow(tournament.id);
      setIsEditing(false);
      if (onSaved) onSaved(nextValues);
    } catch (error) {
      console.error("Error updating tournament settings:", error);
      setErrorMessage("Failed to update tournament settings");
    } finally {
      setSaving(false);
    }
  };

  const handicapLabel = formatTournamentHandicap(tournament);
  const ratioLabel =
    tournament.payoutRatioNumerator && tournament.payoutRatioDenominator
      ? `${tournament.payoutRatioNumerator}:${tournament.payoutRatioDenominator} (1 in ${tournament.payoutRatioDenominator} bowlers get paid)`
      : "Not set";

  return (
    <div className={framed ? "card-board p-6 space-y-6" : "space-y-6"}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="heading-display text-xl">Tournament details</h2>
          <p className="text-sm text-brand-muted mt-1">
            Same fields as the overview flyer: name, date, scoring, and payout
            ratio.
          </p>
        </div>
        {!isEditing ? (
          <SecondaryButton
            type="button"
            onClick={handleStartEdit}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm flex-shrink-0"
          >
            Edit
          </SecondaryButton>
        ) : null}
      </div>

      {errorMessage ? (
        <InlineAlert
          type="error"
          title="There was a problem"
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      ) : null}

      {isEditing ? (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-brand-charcoal mb-1">
                Name
              </label>
              <input
                type="text"
                value={fields.name}
                onChange={(e) => setField("name", e.target.value)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-charcoal mb-1">
                Start date
              </label>
              <input
                type="date"
                value={fields.startDate || ""}
                onChange={(e) => setField("startDate", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <h3 className="font-display font-bold text-brand-charcoal mb-2">
              Scoring
            </h3>
            <ScoringTypeToggle
              name="settings-scoring"
              value={fields.scoringType}
              onChange={(value) => setField("scoringType", value)}
            />
            {fields.scoringType === SCORING_HANDICAP ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-brand-muted mb-1">
                      Base
                    </label>
                    <input
                      type="number"
                      value={fields.handicapBase}
                      onChange={(e) => setField("handicapBase", e.target.value)}
                      className={smallInputClass}
                      placeholder="220"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-muted mb-1">
                      Percentage
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={fields.handicapPercentage}
                      onChange={(e) =>
                        setField("handicapPercentage", e.target.value)
                      }
                      className={smallInputClass}
                      placeholder="90"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-brand-muted mb-1">
                      Maximum handicap
                    </label>
                    <input
                      type="number"
                      value={fields.handicapMax}
                      onChange={(e) => setField("handicapMax", e.target.value)}
                      className={smallInputClass}
                      placeholder="50"
                    />
                  </div>
                </div>
                <p className="text-xs text-brand-muted mt-2">
                  Leave Maximum handicap blank for no cap.
                </p>
              </>
            ) : null}
          </div>

          <div>
            <h3 className="font-display font-bold text-brand-charcoal mb-2">
              Payout ratio
            </h3>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                value={fields.payoutRatioNumerator}
                onChange={(e) =>
                  setField("payoutRatioNumerator", e.target.value)
                }
                className="w-20 p-2 text-sm border border-brand-charcoal/20 rounded-lg focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent"
                placeholder="1"
              />
              <span className="text-brand-muted">:</span>
              <input
                type="number"
                value={fields.payoutRatioDenominator}
                onChange={(e) =>
                  setField("payoutRatioDenominator", e.target.value)
                }
                className="w-20 p-2 text-sm border border-brand-charcoal/20 rounded-lg focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent"
                placeholder="6"
              />
              <span className="text-xs text-brand-muted ml-2">
                (e.g., 1:6 means 1 in 6 bowlers get paid)
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <PrimaryButton type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="!px-3 !py-2 text-sm"
            >
              Cancel
            </SecondaryButton>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailRow label="Name" value={tournament.name || "Not set"} />
            <DetailRow
              label="Start date"
              value={
                tournament.startDate
                  ? formatDate(tournament.startDate)
                  : "Not set"
              }
            />
          </div>
          <DetailRow label="Scoring" value={handicapLabel} />
          <DetailRow label="Payout ratio" value={ratioLabel} />
        </div>
      )}
    </div>
  );
};

export default TournamentSettingsForm;
